import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import AttendanceCamera from "./AttendanceCamera";

const LIMITS = {
  blinkThreshold: { min: 0.15, max: 0.4, step: 0.01 },
  challengeTimeout: { min: 2000, max: 8000, step: 500 },
  matchStability: { min: 1, max: 5 },
  headTurnThreshold: { min: 5, max: 25, step: 1 },
};

const PRESETS = {
  LOW: { blinkThreshold: 0.3, challengeTimeout: 3000, matchStability: 1, headTurnThreshold: 8, enableBlink: true, enableHeadTurn: true },
  MEDIUM: { blinkThreshold: 0.25, challengeTimeout: 4000, matchStability: 2, headTurnThreshold: 10, enableBlink: true, enableHeadTurn: true },
  HIGH: { blinkThreshold: 0.22, challengeTimeout: 5000, matchStability: 3, headTurnThreshold: 12, enableBlink: true, enableHeadTurn: true },
};

export default function LivenessSettingsAdmin() {
  const [settings, setSettings] = useState({});
  const [savedSettings, setSavedSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const getDeviceProfile = () => {
    const cores = navigator.hardwareConcurrency || 2;
    if (cores <= 2) return "LOW";
    if (cores <= 6) return "MEDIUM";
    return "HIGH";
  };

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/admin/liveness-settings`);
        let data = res.data || {};

        if (!data.blinkThreshold) {
          const profile = getDeviceProfile();
          data = { ...PRESETS[profile] };
        } else {
          data.enableBlink = data.enableBlink ?? true;
          data.enableHeadTurn = data.enableHeadTurn ?? true;
        }

        setSettings(data);
        setSavedSettings({ ...data });
      } catch (err) {
        console.error(err);
        setMessage({ type: "danger", text: "Failed to load settings" });
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Track changes
  useEffect(() => {
    setHasChanges(JSON.stringify(settings) !== JSON.stringify(savedSettings));
  }, [settings, savedSettings]);

  // Safe change handler - prevents NaN
  const handleChange = (key, value) => {
    setSettings((prev) => {
      const newSettings = { ...prev };

      if (key === "enableBlink" || key === "enableHeadTurn") {
        newSettings[key] = value;                    // boolean
      } 
      else if (key === "blinkThreshold" || key === "headTurnThreshold") {
        newSettings[key] = value === "" || isNaN(Number(value)) 
          ? (key === "blinkThreshold" ? 0.25 : 10)   // fallback when disabled
          : Number(value);
      } 
      else {
        newSettings[key] = Number(value);            // timeout, stability
      }
      return newSettings;
    });
  };

  const applyPreset = (presetKey) => {
    if (!presetKey) return;
    setSettings((prev) => ({ ...prev, ...PRESETS[presetKey] }));
  };

  const validate = () => {
    if (settings.enableBlink && 
        (settings.blinkThreshold < LIMITS.blinkThreshold.min || 
         settings.blinkThreshold > LIMITS.blinkThreshold.max)) {
      return "Blink threshold out of range";
    }
    if (settings.challengeTimeout < LIMITS.challengeTimeout.min || 
        settings.challengeTimeout > LIMITS.challengeTimeout.max) {
      return "Timeout out of range";
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      setMessage({ type: "danger", text: error });
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${process.env.REACT_APP_API}/admin/liveness-settings`, settings);
      setSavedSettings({ ...settings });
      setHasChanges(false);
      setMessage({ type: "success", text: "Settings saved successfully ✅" });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: "danger", text: "Save failed ❌" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...savedSettings });
  };

  const settingsKey = useMemo(() => JSON.stringify(settings), [settings]);

  if (loading) return <Spinner animation="border" className="d-block mx-auto mt-5" />;

  return (
    <Container fluid>
      <Row>
        {/* Live Preview */}
        <Col md={6}>
          <Card className="mt-4">
            <Card.Body>
              <h5>Live Preview</h5>
              <p className="text-muted small mb-3">
                Changes apply live. When a challenge is disabled, its threshold is ignored.
              </p>
              <AttendanceCamera 
                key={settingsKey} 
                livenessSettings={settings} 
              />
            </Card.Body>
          </Card>
        </Col>

        {/* Settings Panel */}
        <Col md={6}>
          <Card className="mt-4">
            <Card.Body>
              <h4>Liveness Settings</h4>

              {message && (
                <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
                  {message.text}
                </Alert>
              )}

              {hasChanges && <Alert variant="warning">⚠️ Unsaved changes — test in preview</Alert>}

              {/* Presets */}
              <Form.Group className="mb-4">
                <Form.Label>Quick Presets</Form.Label>
                <Form.Select onChange={(e) => applyPreset(e.target.value)}>
                  <option value="">Choose preset...</option>
                  <option value="LOW">Low Security</option>
                  <option value="MEDIUM">Medium Security</option>
                  <option value="HIGH">High Security</option>
                </Form.Select>
              </Form.Group>

              {/* Blink */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Blink Threshold {settings.enableBlink ? `(${LIMITS.blinkThreshold.min}–${LIMITS.blinkThreshold.max})` : "(disabled)"}
                </Form.Label>
                <Form.Control
                  type="number"
                  step={LIMITS.blinkThreshold.step}
                  value={settings.blinkThreshold ?? ""}
                  onChange={(e) => handleChange("blinkThreshold", e.target.value)}
                  disabled={!settings.enableBlink}
                />
              </Form.Group>

              {/* Challenge Timeout */}
              <Form.Group className="mb-3">
                <Form.Label>Challenge Timeout (ms)</Form.Label>
                <Form.Control
                  type="number"
                  step={LIMITS.challengeTimeout.step}
                  value={settings.challengeTimeout ?? ""}
                  onChange={(e) => handleChange("challengeTimeout", e.target.value)}
                />
              </Form.Group>

              {/* Match Stability */}
              <Form.Group className="mb-3">
                <Form.Label>Match Stability</Form.Label>
                <Form.Select
                  value={settings.matchStability ?? 2}
                  onChange={(e) => handleChange("matchStability", e.target.value)}
                >
                  {[1, 2, 3, 4, 5].map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Head Turn */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Head Turn Threshold {settings.enableHeadTurn ? `(${LIMITS.headTurnThreshold.min}–${LIMITS.headTurnThreshold.max})` : "(disabled)"}
                </Form.Label>
                <Form.Control
                  type="number"
                  step={LIMITS.headTurnThreshold.step}
                  value={settings.headTurnThreshold ?? ""}
                  onChange={(e) => handleChange("headTurnThreshold", e.target.value)}
                  disabled={!settings.enableHeadTurn}
                />
              </Form.Group>

              {/* Toggles */}
              <Row className="mb-4">
                <Col>
                  <Form.Check
                    type="switch"
                    label="Enable Blink Challenge"
                    checked={!!settings.enableBlink}
                    onChange={(e) => handleChange("enableBlink", e.target.checked)}
                  />
                </Col>
                <Col>
                  <Form.Check
                    type="switch"
                    label="Enable Head Turn Challenge"
                    checked={!!settings.enableHeadTurn}
                    onChange={(e) => handleChange("enableHeadTurn", e.target.checked)}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleReset} disabled={!hasChanges}>
                  Reset
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={!hasChanges || saving}>
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}